import React, { FC, useCallback, useEffect, useState } from 'react';
import { AccordionDetails as MuiAccordionDetails, Box } from '@material-ui/core';
import LazyLoad from 'react-lazy-load';
import { BlockResize, LTI } from '../../../../../shared/components';
import { Exercise, ExerciseType } from '../../../../../../config/api/models/Exercise';
import AlgebraKitExercise from '../../../../../shared/components/AlgebraKit/AlgebraKitExercise';
import { UserBlockAnswers } from 'src/config/api/models/Answers';
import { useAppDispatch, useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';
import { patchBlock } from 'src/modules/shared/slices/blocks/api/blocks';

type AccordionDetailsProps = {
  exercise: Exercise;
  expanded: string | false;
  lazyLoad?: boolean;
  userAnswersFromPage?: UserBlockAnswers;
};

const DEFAULT_H5P_HEIGHT: number = 750;

export const AccordionDetails: FC<AccordionDetailsProps> = ({
  exercise,
  expanded,
  lazyLoad = false,
  userAnswersFromPage,
}) => {
  const dispatch = useAppDispatch();
  const currentBookId = useAppSelector((state) => state.trajectories.currentBookId);
  const currentPageId = useAppSelector((state) => state.trajectories.currentPageId);
  const currentSectionCode = useAppSelector((state) => state.trajectories.currentSectionCode);
  const showPresentationMode = useAppSelector(
    (state) => state.presentationMode.showPresentationMode
  );
  const viewMode = useAppSelector((state) => state.trajectories.viewMode);
  const [exerciseHeight, setExerciseHeight] = useState<number | undefined>(
    exercise.client_settings?.height
  );
  const [hasResized, setHasResized] = useState<boolean>(false);

  const handleInitialHeight = useCallback(() => {
    if (exercise.client_settings?.height && exercise.client_settings?.height !== 0) {
      setExerciseHeight(exercise.client_settings?.height);
    } else {
      setExerciseHeight(DEFAULT_H5P_HEIGHT);
    }
  }, [exercise.client_settings?.height]);

  useEffect(() => {
    handleInitialHeight();
  }, [handleInitialHeight]);

  const handleOnFrameResize = useCallback(
    (height: number) => {
      if (height !== exerciseHeight) {
        setExerciseHeight(height);
      }
      if (!hasResized || exercise.type !== 'h5p') {
        setHasResized(true);
        if (
          exerciseHeight !== height &&
          viewMode === 'edit' &&
          (currentSectionCode === '0' || currentSectionCode === '1')
        ) {
          dispatch(
            patchBlock({
              bookId: currentBookId!,
              pageId: currentPageId!,
              blockId: exercise.id,
              dontUpdateBlocks: true,
              payload: {
                client_settings: {
                  ...exercise.client_settings,
                  height,
                },
              },
            })
          );
        }
      }
    },
    [
      currentBookId,
      currentPageId,
      currentSectionCode,
      dispatch,
      exercise,
      exerciseHeight,
      hasResized,
      viewMode,
    ]
  );

  const LTIWrapper = (
    <LTI
      type={(exercise as Exercise).type}
      exercise={exercise}
      isVisible={expanded === `exercise_${exercise.id}` || expanded === `all`}
      onFrameResize={handleOnFrameResize}
    />
  );

  if (
    (exercise as Exercise).type === ExerciseType.ALGEBRA_KIT &&
    exercise.algebra_kit_exercise_id
  ) {
    return (
      <MuiAccordionDetails>
        <AlgebraKitExercise
          userAnswersFromPage={userAnswersFromPage}
          algebraKitExerciseId={exercise.algebra_kit_exercise_id}
        />
      </MuiAccordionDetails>
    );
  }

  if ((exercise as Exercise).type === ExerciseType.TEXT_EXERCISE) {
    return (
      <MuiAccordionDetails>
        <div dangerouslySetInnerHTML={{ __html: exercise.description ?? '' }} />
      </MuiAccordionDetails>
    );
  }

  return (
    <MuiAccordionDetails>
      {!showPresentationMode &&
        viewMode === 'read' &&
        (expanded === `exercise_${exercise.id}` || expanded === `all`) && (
          <Box width="100%" height={exerciseHeight}>
            <LazyLoad height="100%" offset={lazyLoad ? 800 : undefined}>
              {LTIWrapper}
            </LazyLoad>
          </Box>
        )}
      {showPresentationMode &&
        viewMode === 'read' &&
        (expanded === `exercise_${exercise.id}` || expanded === `all`) && (
          <Box width="100%" height={exerciseHeight}>
            {LTIWrapper}
          </Box>
        )}
      {viewMode === 'edit' && (
        <BlockResize
          height={exerciseHeight ?? DEFAULT_H5P_HEIGHT}
          onHeightChange={(newHeight) => {
            handleOnFrameResize(newHeight);
          }}
        >
          <LazyLoad height="100%">{LTIWrapper}</LazyLoad>
        </BlockResize>
      )}
    </MuiAccordionDetails>
  );
};
